import React from 'react';
import { styles } from './SupportSearchResults.css';
import { Link } from '../../PlatformFunctionality';
import { HelpAndSupportSearch } from '../HelpAndSupportSearch/HelpAndSupportSearch';
import { useQuery } from 'react-query';
import { ContentfulSupportArticle } from '@jbhi-fi/apis/src/contentful/types';
import { usePublicApiClient } from '../../hooks/usePublicApiClient';
import Spinner from '@jbhi-fi/lanyard-ui/components/Spinner';

export interface SupportSearchResultsProps {
  searchValue?: string;
}

const extractText = (nodes: Array<any>): string => {
  return nodes
    .map(node => {
      if (node.nodeType === 'text') return node.value || '';
      if (Array.isArray(node.content)) return extractText(node.content);
      return '';
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const getSummaryWithHighlight = (text: string, searchTerm?: string, wordLimit = 25): React.ReactNode => {
  const escaped = searchTerm ? searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : '';
  const regex = new RegExp(`(${escaped})`, 'gi');

  const words = text.split(/\s+/);
  const limitedText = words.slice(0, wordLimit).join(' ');
  const truncated = words.length > wordLimit ? `${limitedText}...` : limitedText;

  const parts = truncated.split(regex);

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <span key={i}>
            <strong>{part}</strong>
          </span>
        ) : (
          part
        )
      )}
    </>
  );
};

export const SupportSearchResults = ({ searchValue }: SupportSearchResultsProps): JSX.Element => {
  const { contentfulThemeApiClient } = usePublicApiClient();
  const { data: searchResults, isLoading } = useQuery({
    enabled: !!searchValue && searchValue.length > 1,
    queryKey: ['HelpAndSupportSearchRsults', 'searchResults', searchValue],
    queryFn: async () => {
      const response = await contentfulThemeApiClient?.getEntries<ContentfulSupportArticle>({
        content_type: 'supportArticle',
        'fields.heading[match]': searchValue || '',
        select: 'fields.heading,fields.urlSlug,fields.mainBody,fields.seoPageDescription',
        include: '1',
      });

      if (!response?.data?.items) return;

      return response.data?.items
        .map(article => {
          const description =
            article?.fields?.seoPageDescription || extractText(article?.fields?.mainBody?.content || []);

          return {
            title: article?.fields?.heading || '',
            url: article?.fields?.urlSlug || '',
            summary: getSummaryWithHighlight(description, searchValue),
          };
        })
        .filter(Boolean);
    },
    refetchOnWindowFocus: false,
  });

  return (
    <div>
      <h1>Search results</h1>
      <div className={styles.search}>
        <HelpAndSupportSearch searchKey={searchValue || ''} />
      </div>
      {isLoading ? (
        <div className={styles.loader} data-testid="support-search-loader">
          <Spinner />
        </div>
      ) : (
        <div>
          {searchResults?.length ? (
            <>
              <h2 className={styles.searchResultsTitle}>{`${searchResults.length} result${
                searchResults.length === 1 ? '' : 's'
              } for "${searchValue}"`}</h2>
              <div data-testid="search-results">
                {searchResults?.map(
                  result =>
                    result && (
                      <div key={result.url} className={styles.searchResult}>
                        <Link href={result.url} data-testid="search-result-link" className={styles.searchResultLink}>
                          {result.title}
                        </Link>
                        <div>{result.summary}</div>
                      </div>
                    )
                )}
              </div>
            </>
          ) : null}
          {!searchResults?.length ? (
            <>
              <p data-testid="search-no-results">
                No results found for <strong>{` "${searchValue}"`}</strong>.
              </p>
              <p>
                Try searching a different word or <Link href="/">browse our support categories.</Link>
              </p>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};
